// Rich Text Renderer for Contentful Rich Text fields
import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { getImageUrl } from '../services/contentful';

/**
 * Renders Contentful Rich Text content with proper styling
 * @param {object} content - The rich text document from Contentful
 * @param {string} className - Additional CSS classes
 */
export const RichTextRenderer = ({ content, className = '' }) => {
  if (!content) return null;

  const options = {
    renderMark: {
      [MARKS.BOLD]: (text) => <strong className="font-bold">{text}</strong>,
      [MARKS.ITALIC]: (text) => <em className="italic">{text}</em>,
      [MARKS.UNDERLINE]: (text) => <u className="underline">{text}</u>,
      [MARKS.CODE]: (text) => (
        <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">
          {text}
        </code>
      ),
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <p className="mb-4 leading-relaxed text-gray-700">{children}</p>
      ),
      [BLOCKS.HEADING_1]: (node, children) => (
        <h1 className="text-3xl md:text-4xl font-heading font-bold mb-6 mt-8 text-gray-900">
          {children}
        </h1>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4 mt-6 text-gray-900">
          {children}
        </h2>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <h3 className="text-xl md:text-2xl font-heading font-semibold mb-3 mt-5 text-gray-900">
          {children}
        </h3>
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <h4 className="text-lg md:text-xl font-heading font-semibold mb-3 mt-4 text-gray-900">
          {children}
        </h4>
      ),
      [BLOCKS.HEADING_5]: (node, children) => (
        <h5 className="text-base md:text-lg font-heading font-semibold mb-2 mt-4 text-gray-900">
          {children}
        </h5>
      ),
      [BLOCKS.HEADING_6]: (node, children) => (
        <h6 className="text-sm md:text-base font-heading font-semibold mb-2 mt-3 text-gray-900">
          {children}
        </h6>
      ),
      [BLOCKS.UL_LIST]: (node, children) => (
        <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700">
          {children}
        </ul>
      ),
      [BLOCKS.OL_LIST]: (node, children) => (
        <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-700">
          {children}
        </ol>
      ),
      [BLOCKS.LIST_ITEM]: (node, children) => (
        <li className="leading-relaxed">{children}</li>
      ),
      [BLOCKS.QUOTE]: (node, children) => (
        <blockquote className="border-l-4 border-orange-500 pl-4 py-2 my-4 italic bg-gray-50 rounded-r">
          {children}
        </blockquote>
      ),
      [BLOCKS.HR]: () => <hr className="my-8 border-gray-200" />,
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const asset = node.data.target;
        if (!asset) return null;

        const contentType = asset.fields?.file?.contentType || '';
        
        if (contentType.startsWith('image/')) {
          const url = getImageUrl(asset, { width: 800, quality: 80 });
          const title = asset.fields?.title || 'Image';
          const description = asset.fields?.description;
          
          return (
            <figure className="my-6">
              <img
                src={url}
                alt={title}
                className="w-full rounded-lg shadow-md"
                loading="lazy"
              />
              {description && (
                <figcaption className="text-sm text-gray-500 mt-2 text-center">
                  {description}
                </figcaption>
              )}
            </figure>
          );
        }

        if (contentType.startsWith('video/')) {
          const url = `https:${asset.fields.file.url}`;
          return (
            <video
              src={url}
              controls
              className="w-full rounded-lg my-6"
            />
          );
        }

        // For other file types, show a download link
        const url = `https:${asset.fields?.file?.url}`;
        const fileName = asset.fields?.file?.fileName || 'Download';
        return (
          <a
            href={url}
            download
            className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg my-4 text-gray-700"
          >
            📎 {fileName}
          </a>
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        // Handle embedded entries if needed
        const entry = node.data.target;
        if (!entry) return null;

        // You can customize this based on your content types
        return (
          <div className="border rounded-lg p-4 my-4 bg-gray-50">
            <p className="text-sm text-gray-500">Embedded content</p>
          </div>
        );
      },
      [INLINES.HYPERLINK]: (node, children) => (
        <a
          href={node.data.uri}
          target={node.data.uri.startsWith('http') ? '_blank' : '_self'}
          rel="noopener noreferrer"
          className="text-orange-600 hover:text-orange-700 underline"
        >
          {children}
        </a>
      ),
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        // Handle links to other entries
        const entry = node.data.target;
        if (!entry) return <span>{children}</span>;

        // Customize based on your content types and routing
        return (
          <a href={`/${entry.sys.contentType?.sys?.id}/${entry.fields?.slug}`} className="text-orange-600 hover:text-orange-700 underline">
            {children}
          </a>
        );
      },
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        const asset = node.data.target;
        if (!asset) return <span>{children}</span>;

        const url = `https:${asset.fields?.file?.url}`;
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-orange-600 hover:text-orange-700 underline"
          >
            {children}
          </a>
        );
      },
    },
  };

  return (
    <div className={`rich-text-content ${className}`}>
      {documentToReactComponents(content, options)}
    </div>
  );
};

export default RichTextRenderer;
